import { PathStyleExtension } from "@deck.gl/extensions";
import {
    DrawPolygonMode,
    EditableGeoJsonLayer,
    ViewMode,
} from "@deck.gl-community/editable-layers";
import { Matrix4 } from "@math.gl/core";
import { replaceEqualDeep, useQueries } from "@tanstack/react-query";
import centroid from "@turf/centroid";
import { feature, featureCollection } from "@turf/helpers";
import intersect from "@turf/intersect";
import union from "@turf/union";
import type { MultiPolygon, Polygon } from "geojson";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useGeoDataApi } from "../../../hooks";
import {
    emptyFeatureCollection,
    normalizeGEOJSON,
} from "../../../utils/geopatialUtils";
import {
    automaticPanAndZoomAtom,
    mapMapAreaFiltersStateFamily,
} from "../state";
import { useMap } from "./mapState";

const selectedFeatureIndexes: number[] = [];

export function useStable<T>(value: T) {
    const ref = useRef(value);
    const stable = replaceEqualDeep(ref.current, value);
    useEffect(() => {
        ref.current = stable;
    }, [stable]);
    return stable;
}

export const useDrawAndFilterControlsV2 = (mapId: string) => {
    const apiClient = useGeoDataApi();
    const [autoMove] = useAtom(automaticPanAndZoomAtom);
    const { flyTo, debounced } = useMap(mapId);
    const [
        { selectedFilters, drawnShape, filterData, isDrawing, drawingMode },
        setFilterState,
    ] = useAtom(mapMapAreaFiltersStateFamily({ mapId: mapId }));

    // Handle fetching geometries for selected filters
    const presetQueries = useQueries({
        queries: useMemo(
            () =>
                selectedFilters.map((areaId) => ({
                    queryKey: ["areaFilterPreset", areaId],
                    queryFn: async () => {
                        return await apiClient.geodataItemsRetrieve({
                            id: areaId,
                        });
                    },
                    refetchOnWindowFocus: false,
                    staleTime: 0,
                })),
            [selectedFilters, apiClient],
        ),
    });
    const loadingPresets = presetQueries.some((q) => q.isLoading);
    const stableQueryResults = useStable(presetQueries.map((q) => q.data));

    // Debounce output data and apply filters when queries change
    const filterShape = useMemo(() => {
        const validShapes = [];
        for (const item of stableQueryResults) {
            if (item && item.geometry) {
                // This checks for truthy values, so null won't be included
                validShapes.push(item.geometry);
            }
        }

        if (validShapes.length === 0) return undefined;

        if (validShapes.length === 1) {
            return normalizeGEOJSON(validShapes[0]);
        }
        return normalizeGEOJSON(
            union(featureCollection(validShapes.map((s) => feature(s)))),
        );
    }, [stableQueryResults]);

    useEffect(() => {
        if (autoMove && filterShape && filterShape.features.length > 0) {
            const center = centroid(filterShape);
            const lat = center.geometry.coordinates[1];
            const lon = center.geometry.coordinates[0];
            flyTo(lat, lon, 4, true);
        }
    }, [autoMove, filterShape, flyTo]);

    // Set presets action
    const setPresets = useCallback(
        (presets: number[]) => {
            setFilterState((filters) => ({
                ...filters,
                selectedFilters: presets,
                isDrawing: false,
                filterData: true,
                drawnShape: emptyFeatureCollection,
            }));
        },
        [setFilterState],
    );

    // Set custom shape also turns on editing mode
    const setShape = useCallback(
        (shape: any) => {
            setFilterState((filters) => ({
                ...filters,
                selectedFilters: [],
                drawnShape: shape,
                isDrawing: true,
                filterData: false,
                drawingMode: "EDIT",
            }));
            if (autoMove) {
                const center = centroid(shape);
                const lat = center.geometry.coordinates[1];
                const lon = center.geometry.coordinates[0];
                flyTo(lat, lon, 4, true);
            }
        },
        [setFilterState, autoMove, flyTo],
    );

    const startDrawing = () => {
        setFilterState((filters) => ({
            ...filters,
            selectedFilters: [],
            drawnShape: emptyFeatureCollection,
            filterData: false,
            isDrawing: true,
            drawingMode: "DRAW",
        }));
    };

    const stopDrawing = (save: boolean) => {
        if (save) {
            setFilterState((filters) => ({
                ...filters,
                selectedFilters: [],
                isDrawing: false,
                filterData: true,
            }));
        } else {
            setFilterState((filters) => ({
                ...filters,
                selectedFilters: [],
                drawnShape: emptyFeatureCollection,
                filterData: false,
                isDrawing: false,
                drawingMode: "DRAW",
            }));
        }
    };

    // Atomically set a shape and commit it as the active filter in one update.
    // Use this instead of setShape + stopDrawing(true) to avoid race conditions.
    const commitShape = useCallback(
        (shape: any) => {
            setFilterState((filters) => ({
                ...filters,
                selectedFilters: [],
                drawnShape: shape,
                isDrawing: false,
                filterData: true,
                drawingMode: "DRAW",
            }));
            if (autoMove) {
                const center = centroid(shape);
                const lat = center.geometry.coordinates[1];
                const lon = center.geometry.coordinates[0];
                flyTo(lat, lon, 4, true);
            }
        },
        [setFilterState, autoMove, flyTo],
    );

    // Compute drawnShape if presets are selected.
    const drawnShapeComputed = useMemo(() => {
        if (selectedFilters.length > 0) {
            return filterShape;
        }
        return drawnShape;
    }, [filterShape, selectedFilters, drawnShape]);

    // Map drawing layer
    // Drawing on map
    const drawingLayer = useMemo(() => {
        const layers = [
            new (EditableGeoJsonLayer as any)({
                id: "drawing-layer",
                data: isDrawing ? drawnShape : drawnShapeComputed,
                mode: isDrawing ? DrawPolygonMode : ViewMode,
                selectedFeatureIndexes,
                pickable: isDrawing,
                wrapLongitude: true,
                getFillColor: [0, 0, 0, 40],
                getLineWidth: 1,
                getLineColor: [255, 255, 255, 255],
                getDashArray: [20, 20],
                dashJustified: true,
                dashGapPickable: true,
                extensions: [new PathStyleExtension({ dash: true })],
                onEdit: ({ updatedData, editType }) => {
                    if (editType === "addFeature") {
                        setFilterState((filters) => ({
                            ...filters,
                            drawnShape: updatedData,
                            drawingMode: "EDIT",
                        }));
                    }
                },
                modelMatrix: new Matrix4().scale([1, 1, 0]),
            }),
        ];

        if (!isDrawing) {
            layers.unshift(
                new (EditableGeoJsonLayer as any)({
                    id: "drawing-layer-highlight",
                    data: drawnShape,
                    mode: ViewMode,
                    selectedFeatureIndexes,
                    pickable: false,
                    getFillColor: [0, 0, 0, 0],
                    getLineWidth: 3,
                    getLineColor: [0, 0, 0, 255],
                    getDashArray: [20, 20],
                    dashJustified: true,
                    dashGapPickable: true,
                    modelMatrix: new Matrix4().scale([1, 1, 0]),
                }),
            );
        }
        return layers;
    }, [drawnShape, isDrawing, drawnShapeComputed, setFilterState]);

    const filterByArea = useMemo(() => {
        if (!filterData) {
            return {
                drawnShape: undefined,
                selectedFilters: undefined,
            };
        }

        let cleanShapeGeometry: Polygon | MultiPolygon;
        const shapes = drawnShape
            ? drawnShape.features?.filter(
                  (f) =>
                      f.geometry.type === "Polygon" ||
                      f.geometry.type === "MultiPolygon",
              )
            : [];
        if (shapes.length > 0) {
            if (shapes.length === 1 && shapes[0].geometry.type === "Polygon") {
                // Keep single polygon as is
                cleanShapeGeometry = shapes[0].geometry;
            } else {
                // Combine multiple shapes or multipolygons into a single multipolygon
                const coordinates = [];

                shapes.forEach((feature) => {
                    const geometry = feature.geometry;

                    if (geometry.type === "Polygon") {
                        coordinates.push(geometry.coordinates);
                    } else if (geometry.type === "MultiPolygon") {
                        coordinates.push(...geometry.coordinates);
                    }
                });

                cleanShapeGeometry = {
                    type: "MultiPolygon",
                    coordinates: coordinates,
                };
            }
        }
        return {
            selectedFilters:
                selectedFilters.length > 0 ? selectedFilters : undefined,
            drawnShape: cleanShapeGeometry,
        };
    }, [selectedFilters, drawnShape, filterData]);

    const areaToFetch = useMemo(() => {
        if (!debounced.areaOnScreen) {
            return undefined;
        }
        if (!drawnShape || drawnShape.features.length === 0 || !filterData) {
            return JSON.stringify(debounced.areaOnScreen);
        }
        return JSON.stringify(
            intersect(
                featureCollection([
                    feature(debounced.areaOnScreen),
                    feature(drawnShape.features[0].geometry as any),
                ]),
            )?.geometry,
        );
    }, [debounced.areaOnScreen, filterData, drawnShape]);

    return {
        selectedFilters,
        isDrawing,
        drawingLayer,
        startDrawing,
        stopDrawing,
        commitShape,
        drawingMode,
        areaToFetch,
        filterData,
        drawnShape: drawnShapeComputed,
        setPresets,
        setShape,
        loadingPresets,
        filterByArea: useStable(filterByArea),
    };
};
